import React from 'react';
import { Result, Button, Card } from 'antd';
import { ExclamationCircleOutlined, ReloadOutlined, HomeOutlined } from '@ant-design/icons';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null, 
            errorInfo: null,
            errorId: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details
        const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        this.setState({
            error,
            errorInfo,
            errorId
        });

        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.error('ErrorBoundary caught an error:', error, errorInfo);
        }

        // In production, you might want to send this to an error reporting service
        // Example: Sentry.captureException(error, { extra: errorInfo });
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    handleRetry = () => {
        this.setState({ 
            hasError: false, 
            error: null, 
            errorInfo: null,
            errorId: null
        });
    };

    render() {
        if (this.state.hasError) {
            const { errorId } = this.state;
            
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                    <Card className="max-w-md w-full">
                        <Result
                            status="error"
                            title="Something went wrong"
                            subTitle={
                                <div className="text-center">
                                    <p className="mb-2">
                                        We're sorry, but something unexpected happened.
                                    </p>
                                    {errorId && (
                                        <p className="text-sm text-gray-500">
                                            Error ID: {errorId}
                                        </p>
                                    )}
                                </div>
                            }
                            extra={[
                                <Button 
                                    type="primary" 
                                    key="retry" 
                                    icon={<ReloadOutlined />}
                                    onClick={this.handleRetry}
                                    className="mr-2"
                                >
                                    Try Again
                                </Button>,
                                <Button 
                                    key="reload" 
                                    icon={<ReloadOutlined />}
                                    onClick={this.handleReload}
                                    className="mr-2"
                                >
                                    Reload Page
                                </Button>,
                                <Button 
                                    key="home" 
                                    icon={<HomeOutlined />}
                                    onClick={this.handleGoHome}
                                >
                                    Go Home
                                </Button>
                            ]}
                        />
                        
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
                                <h4 className="text-red-800 font-semibold mb-2">
                                    <ExclamationCircleOutlined className="mr-1" />
                                    Development Error Details:
                                </h4>
                                <pre className="text-xs text-red-700 overflow-auto max-h-40">
                                    {this.state.error.toString()}
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </div>
                        )}
                    </Card>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;